/**
 * OfflineStorage.ts - IndexedDB-based offline storage service
 *
 * Provides persistent storage for:
 * - Cached user data (balance, transactions, forecast)
 * - Pending actions queue (decisions, categorizations)
 * - Data freshness tracking
 */

// Database configuration
const DB_NAME = 'flowincash-offline';
const DB_VERSION = 1;

// Store names
export const STORES = {
  USER_DATA: 'userData',
  PENDING_ACTIONS: 'pendingActions',
  SYNC_METADATA: 'syncMetadata',
} as const;

// Types
export interface CachedUserData {
  key: string;
  data: unknown;
  timestamp: number;
  expiresAt?: number;
}

export interface PendingAction {
  id: string;
  type: 'purchase_decision' | 'categorization' | 'transaction_update';
  payload: unknown;
  createdAt: number;
  retryCount: number;
  status: 'pending' | 'syncing' | 'failed';
  errorMessage?: string;
}

export interface SyncMetadata {
  key: string;
  lastSyncedAt: number;
  syncStatus: 'idle' | 'syncing' | 'error';
  errorMessage?: string;
}

// Known data keys for type safety
export type DataKey =
  | 'balance'
  | 'transactions'
  | 'forecast'
  | 'categories'
  | 'purchaseQueue'
  | 'userProfile'
  | 'accounts';

class OfflineStorageService {
  private db: IDBDatabase | null = null;
  private dbReady: Promise<IDBDatabase>;
  private dbReadyResolve!: (db: IDBDatabase) => void;
  private dbReadyReject!: (error: Error) => void;

  constructor() {
    this.dbReady = new Promise((resolve, reject) => {
      this.dbReadyResolve = resolve;
      this.dbReadyReject = reject;
    });
    this.initDatabase();
  }

  private initDatabase(): void {
    if (typeof indexedDB === 'undefined') {
      console.warn('IndexedDB not supported');
      this.dbReadyReject(new Error('IndexedDB not supported'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      this.dbReadyReject(request.error || new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      this.db = request.result;
      this.dbReadyResolve(this.db);
      console.log('IndexedDB initialized successfully');
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create user data store
      if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
        const userDataStore = db.createObjectStore(STORES.USER_DATA, { keyPath: 'key' });
        userDataStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Create pending actions store
      if (!db.objectStoreNames.contains(STORES.PENDING_ACTIONS)) {
        const pendingStore = db.createObjectStore(STORES.PENDING_ACTIONS, { keyPath: 'id' });
        pendingStore.createIndex('status', 'status', { unique: false });
        pendingStore.createIndex('createdAt', 'createdAt', { unique: false });
        pendingStore.createIndex('type', 'type', { unique: false });
      }

      // Create sync metadata store
      if (!db.objectStoreNames.contains(STORES.SYNC_METADATA)) {
        db.createObjectStore(STORES.SYNC_METADATA, { keyPath: 'key' });
      }
    };
  }

  private async getDb(): Promise<IDBDatabase> {
    return this.dbReady;
  }

  // ========== User Data Operations ==========

  /**
   * Store cached user data with optional TTL
   */
  async setCachedData<T>(key: DataKey, data: T, ttlMs?: number): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.USER_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.USER_DATA);

      const record: CachedUserData = {
        key,
        data,
        timestamp: Date.now(),
        expiresAt: ttlMs ? Date.now() + ttlMs : undefined,
      };

      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get cached user data (returns null if expired or not found)
   */
  async getCachedData<T>(key: DataKey): Promise<{ data: T; timestamp: number } | null> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.USER_DATA], 'readonly');
      const store = transaction.objectStore(STORES.USER_DATA);
      const request = store.get(key);

      request.onsuccess = () => {
        const record = request.result as CachedUserData | undefined;
        if (!record) {
          resolve(null);
          return;
        }

        // Check expiration
        if (record.expiresAt && Date.now() > record.expiresAt) {
          // Data expired, clean it up
          this.deleteCachedData(key).catch(console.error);
          resolve(null);
          return;
        }

        resolve({
          data: record.data as T,
          timestamp: record.timestamp,
        });
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete cached data by key
   */
  async deleteCachedData(key: DataKey): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.USER_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.USER_DATA);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get data freshness (time since last cache)
   */
  async getDataFreshness(key: DataKey): Promise<{ age: number; timestamp: number } | null> {
    const cached = await this.getCachedData(key);
    if (!cached) return null;

    return {
      age: Date.now() - cached.timestamp,
      timestamp: cached.timestamp,
    };
  }

  /**
   * Clear all cached user data
   */
  async clearAllCachedData(): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.USER_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.USER_DATA);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ========== Pending Actions Operations ==========

  /**
   * Add an action to the pending queue
   */
  async addPendingAction(action: Omit<PendingAction, 'id' | 'createdAt' | 'retryCount' | 'status'>): Promise<string> {
    const db = await this.getDb();
    const id = `${action.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);

      const record: PendingAction = {
        ...action,
        id,
        createdAt: Date.now(),
        retryCount: 0,
        status: 'pending',
      };

      const request = store.add(record);
      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all pending actions
   */
  async getPendingActions(status?: PendingAction['status']): Promise<PendingAction[]> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_ACTIONS], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);

      let request: IDBRequest;
      if (status) {
        const index = store.index('status');
        request = index.getAll(status);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update pending action status
   */
  async updatePendingAction(id: string, updates: Partial<Pick<PendingAction, 'status' | 'retryCount' | 'errorMessage'>>): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const record = getRequest.result as PendingAction | undefined;
        if (!record) {
          reject(new Error(`Pending action ${id} not found`));
          return;
        }

        const updated: PendingAction = { ...record, ...updates };
        const putRequest = store.put(updated);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Remove a pending action (after successful sync)
   */
  async removePendingAction(id: string): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get count of pending actions
   */
  async getPendingActionsCount(): Promise<number> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_ACTIONS], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      const index = store.index('status');
      const request = index.count('pending');

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all pending actions (use with caution)
   */
  async clearPendingActions(): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ========== Sync Metadata Operations ==========

  /**
   * Update sync metadata for a data key
   */
  async updateSyncMetadata(key: DataKey, metadata: Partial<Omit<SyncMetadata, 'key'>>): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_METADATA], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_METADATA);
      const getRequest = store.get(key);

      getRequest.onsuccess = () => {
        const existing = getRequest.result as SyncMetadata | undefined;
        const record: SyncMetadata = {
          key,
          lastSyncedAt: existing?.lastSyncedAt || 0,
          syncStatus: existing?.syncStatus || 'idle',
          ...metadata,
        };

        const putRequest = store.put(record);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Get sync metadata for a data key
   */
  async getSyncMetadata(key: DataKey): Promise<SyncMetadata | null> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_METADATA], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_METADATA);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get last sync time across all data
   */
  async getLastSyncTime(): Promise<number> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_METADATA], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_METADATA);
      const request = store.getAll();

      request.onsuccess = () => {
        const records = request.result as SyncMetadata[];
        if (!records.length) {
          resolve(0);
          return;
        }
        const maxTime = Math.max(...records.map(r => r.lastSyncedAt));
        resolve(maxTime);
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();

// Helper function to format data age
export function formatDataAge(timestampMs: number): string {
  const ageMs = Date.now() - timestampMs;
  const seconds = Math.floor(ageMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}
